import { useTemplateStore } from '@/store/template.store';
import { createThumbnailFromBat, canvasToBlob, generateImageWithWatermarks } from '@/littleWedding/canvasMaterialsManager';
import { getCMJNbat } from '@/api/pdf.api';
import { saveToLocalStorage } from '@/utils/localStorageSave';
import { useConfigStore } from '@/store/config.store';
import { EProductFormat } from '@/typescript/enums/EProductFormat';
import { dataStore } from '@/commons/DataStore';
import { initCanvas } from '@/littleWedding/canvasBuilder';
import { captureException } from '@sentry/vue';
import { storeToRefs } from 'pinia';
import { useTemplateRef } from 'vue';
import type { ICart } from '@/typescript/interfaces/ICart';
import type { WFabricWorld } from '@/littleWedding/WFabricWorld';

function toErrorMessage(error: unknown): string {
    // Never expose the raw "TypeError: ..." prefix to the customer, the host page displays this string as is
    return error instanceof Error ? error.message : String(error);
}

async function addToCart(projectId?: string, isPreview: boolean = false): Promise<ICart> {
    const templateStore = useTemplateStore();
    const { isAdmin, printHeight, printWidth, productFormat, productType } = storeToRefs(useConfigStore());
    let blobVerso: Blob | null = null;
    let batPreviewVerso;
    const { wFabricWorldRecto, wFabricWorldVerso } = dataStore;

    if (!wFabricWorldRecto) {
        throw new Error('Fabric world does not exist');
    }
    if (!isAdmin.value && projectId) {
        try {
            await saveToLocalStorage(templateStore.selectedTemplate, projectId);
        } catch (e) {
            // The local backup only serves later edition of the project, it must never block the order
            console.warn('Failed to save the project locally', e);
            captureException(e, { extra: { step: 'saveToLocalStorage', projectId, productType: productType.value } });
        }
    }
    const blobRecto = await canvasToBlob({ fabricWorld: wFabricWorldRecto, isPreview });
    if (!blobRecto) {
        throw new Error('Failed to convert canvas to blob');
    }
    if (productFormat.value === EProductFormat.RECTO_VERSO && wFabricWorldVerso) {
        if (!wFabricWorldVerso.isInitialized) {
            const fabricElementRef = useTemplateRef<HTMLCanvasElement | null>(`fabricElementRef-verso`);
            await initCanvas(fabricElementRef, 'verso');
        }

        blobVerso = await canvasToBlob({ fabricWorld: wFabricWorldVerso, isPreview });
    }

    const batPreview = await generateImageWithWatermarks(wFabricWorldRecto);
    if (!batPreview) {
        throw new Error('Failed to convert canvas to image preview URL');
    }

    if (blobVerso && wFabricWorldVerso) {
        batPreviewVerso = await generateImageWithWatermarks(wFabricWorldVerso);
    }

    const imgBatWidth = printWidth.value;
    const imgBatHeight = printHeight.value;

    let error = false;
    let thumbnail = new Blob();
    let thumbnailVerso = new Blob();
    let templateId = 0;
    let pdf = new Blob();
    let errorMessage = '';

    try {
        const screenBlobRecto = await createThumbnailFromBat({
            fabricWorld: wFabricWorldRecto as WFabricWorld,
        });
        if (!screenBlobRecto) {
            throw new Error('Failed to create thumbnail from BAT');
        }

        thumbnail = screenBlobRecto;
        templateId = templateStore.selectedTemplate.id || 0;

        if (batPreviewVerso) {
            const screenBlobVerso = await createThumbnailFromBat({
                fabricWorld: wFabricWorldVerso as WFabricWorld,
            });
            if (!screenBlobVerso) {
                throw new Error('Failed to create thumbnail from BAT verso');
            }
            thumbnailVerso = screenBlobVerso;
        }
        pdf = await getCMJNbat(imgBatWidth, imgBatHeight, blobRecto, blobVerso || undefined);
    } catch (e) {
        error = true;
        errorMessage = toErrorMessage(e);
        captureException(e, {
            extra: {
                step: 'addToCart',
                projectId,
                productType: productType.value,
                productFormat: productFormat.value,
                printWidth: imgBatWidth,
                printHeight: imgBatHeight,
                rectoBytes: blobRecto.size,
                versoBytes: blobVerso?.size ?? 0,
            },
        });
    }
    return {
        error,
        error_message: errorMessage,
        batPreview,
        batPreviewVerso,
        thumbnail,
        thumbnailVerso,
        template_id: templateId,
        pdf: pdf,
        project_id: projectId ?? '',
    };
}

async function debugCartPdf(projectId?: string) {
    const cart = await addToCart(projectId, false);
    if (cart.error) {
        console.error('Failed to generate PDF:', cart.error_message);
        return;
    }
    const pdfBlob = cart.pdf;
    if (pdfBlob && pdfBlob.size > 0) {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = 'bat.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(pdfUrl);
    }
}

export { addToCart, debugCartPdf };
